/**
 * Sample data for the designer of a template that has no table to read columns from: an
 * `api` template (its data arrives in each document API request), or a source template
 * whose rows the API cannot read with the caller's own connection.
 *
 * Two directions, both through the document API's own reading of JSON so a name can never
 * differ from what a real request fills:
 *
 * - `sampleDataFields(sample)`: the field list a pasted example request names. Keys are
 *   named by `sanitizeFieldName()` as `documentRecordFromJson()` names them, and a sample
 *   that the document API would refuse (two keys that sanitize alike, a mixed list) is
 *   refused here with the same error.
 * - `sampleDataOfFields(fields)`: an example to start from, built from a field list, each
 *   text value the field's own name so a preview shows where every field lands.
 */

import type { TemplateFieldInfo } from '../api'
import { sanitizeFieldName } from '../fields'
import { DOCUMENT_API_RESERVED_NAMES, documentRecordFromJson, type DocumentApiError } from '../documentApi'

/** Largest sample, as JSON, the API stores beside a template's design. */
export const MAX_DESIGN_SAMPLE_BYTES = 128 * 1024

/** A URL a picture block can take: an http(s) address ending in an image file's extension. */
const PICTURE_URL = /^https?:\/\/\S+\.(?:png|jpe?g|gif|webp|bmp)(?:[?#]\S*)?$/i

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

/** The variables every document API record carries (set by AutomaDoc, never by the request). */
const SYSTEM_FIELDS: TemplateFieldInfo[] = DOCUMENT_API_RESERVED_NAMES.map(name => ({
  name,
  variable: name,
  fieldType: 'text' as const,
  isSystem: true,
}))

/**
 * The fields a sample request's `data` names. A list of items (a template making one
 * document from many) is read by its first item, which is what each row of `{#Records}`
 * holds. Objects are linked records whose fields carry their path (`Customer.Name`), a list
 * of objects is a loop whose row fields are the row's own (`Qty`), as every source lists
 * them; a URL to an image file is a picture, anything else text.
 */
export function sampleDataFields(sample: unknown): { ok: true; fields: TemplateFieldInfo[] } | { ok: false; error: DocumentApiError } {
  const item = Array.isArray(sample) ? sample[0] : sample
  // A list is items of one shape: each one an object, as the document API takes them.
  if (!isPlainObject(item) || (Array.isArray(sample) && !sample.every(isPlainObject))) {
    return { ok: false, error: { code: 'DATA_NOT_OBJECT', error: 'Sample data must be a JSON object, or a list of them', path: 'data' } }
  }
  // Read exactly as a request is read: what it refuses is refused here, by the same words.
  const read = documentRecordFromJson(item, { imagePaths: new Set(), tokenPrefix: 'sample_', path: 'data' })
  if (!read.ok) return { ok: false, error: read.error }

  /** `top`: the record itself, where AutomaDoc's own names replace the sample's (not a loop row's). */
  const describe = (obj: Record<string, unknown>, prefix: string, top = false): TemplateFieldInfo[] => {
    const fields: TemplateFieldInfo[] = []
    for (const [key, value] of Object.entries(obj)) {
      const name = sanitizeFieldName(key)
      if (top && (DOCUMENT_API_RESERVED_NAMES as readonly string[]).includes(name)) continue
      const variable = prefix ? `${prefix}.${name}` : name
      if (isPlainObject(value)) {
        fields.push({ name: key, variable, fieldType: 'linked', nested: describe(value, variable) })
      } else if (Array.isArray(value) && value.length > 0 && value.every(isPlainObject)) {
        // Every row's keys: a column one row leaves out is still a column of the loop.
        const columns = Object.assign({}, ...(value as Record<string, unknown>[]))
        fields.push({ name: key, variable, fieldType: 'loop', nested: describe(columns, '') })
      } else {
        const picture = typeof value === 'string' && PICTURE_URL.test(value.trim())
        fields.push({ name: key, variable, fieldType: picture ? 'image' : 'text' })
      }
    }
    return fields
  }

  return { ok: true, fields: [...describe(item, '', true), ...SYSTEM_FIELDS] }
}

/**
 * An example `data` object for a field list: text as the field's own name, a picture empty
 * (to be given an address), a linked record as an object, a loop as one row. System fields are
 * left out: AutomaDoc sets them. Keys are the variables' last segment, which
 * `documentRecordFromJson()` maps back to themselves.
 */
export function sampleDataOfFields(fields: TemplateFieldInfo[]): Record<string, unknown> {
  const build = (list: TemplateFieldInfo[], depth: number): Record<string, unknown> => {
    const out: Record<string, unknown> = {}
    for (const field of list) {
      if (field.isSystem) continue
      const key = field.variable.split('|')[0].trim().split('.').pop()
      if (!key) continue
      if (field.fieldType === 'linked') out[key] = depth < 5 ? build(field.nested ?? [], depth + 1) : {}
      else if (field.fieldType === 'loop') out[key] = depth < 5 ? [build(field.nested ?? [], depth + 1)] : []
      // No picture of ours stands in: an address the customer pastes is the one to test.
      else if (field.fieldType === 'image') out[key] = ''
      else out[key] = field.name
    }
    return out
  }
  return build(fields, 0)
}
